import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { createReferral, fetchReferralOverview, fetchReferralProgram } from '../services/referrals';

const STATUS_COLOR = {
  approved: 'success',
  in_review: 'info',
  pending: 'default',
  rejected: 'error',
};

const formatMoney = (value) => {
  if (value === null || value === undefined) return '-';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const rangeLabel = (min, max) => {
  if (max === null || max === undefined) return `${formatMoney(min)}+`;
  return `${formatMoney(min)} - ${formatMoney(max)}`;
};

const statusLabel = (value) => {
  if (!value) return '';
  const clean = value.replace('_', ' ');
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

const FriendReferralsPage = () => {
  const [program, setProgram] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    friend_name: '',
    friend_email: '',
    friend_phone: '',
    note: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [programRes, overviewRes] = await Promise.all([
        fetchReferralProgram(),
        fetchReferralOverview({ limit: 200 }),
      ]);
      setProgram(programRes);
      setReferrals(overviewRes?.referrals || []);
    } catch (e) {
      console.error('Failed to load referrals', e);
      setError('Unable to load friend referral data right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.friend_name.trim()) {
      setError('Please add a name for your friend.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await createReferral({
        friend_name: form.friend_name,
        friend_email: form.friend_email || undefined,
        friend_phone: form.friend_phone || undefined,
        note: form.note || undefined,
      });
      setSuccess('Referral saved. You can track it in the list below.');
      setForm({ friend_name: '', friend_email: '', friend_phone: '', note: '' });
      await loadData();
    } catch (e) {
      console.error('Create referral failed', e);
      const message = e?.response?.data?.message || 'Unable to save referral.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f6f8fc', minHeight: '100vh', pb: 6 }}>
      {loading && (
        <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 10 }} />
      )}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8} sx={{ mx: 'auto' }}>
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e6ebf5' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    What is the Friend Referrals Programme?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#41506b', lineHeight: 1.6 }}>
                    Invite friends to open and fund an Onsa FX account. Once they meet the deposit and
                    minimum lot cycle, both of you unlock withdrawable bonus and Onsa Credits that scale
                    with your total approved friends.
                  </Typography>
                  <Stack spacing={1.25} sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TrendingUpIcon fontSize="small" sx={{ color: '#04477e' }} />
                      <Typography variant="body2" sx={{ color: '#1f2b3d' }}>
                        Higher deposit bands and more approved friends mean bigger percentages.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                      <Typography variant="body2" sx={{ color: '#1f2b3d' }}>
                        Bonuses are withdrawable once the minimum lot cycle is validated.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <InfoOutlinedIcon fontSize="small" sx={{ color: '#ef6c00' }} />
                      <Typography variant="body2" sx={{ color: '#1f2b3d' }}>
                        Onsa Credits on your own deposits unlock at 5+ and 10+ approved friends.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {program && (
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e6ebf5' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} rowGap={1}>
                  <Typography variant="h6" fontWeight={700}>Reward matrix</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rewards scale with your approved friends and each friend's deposit band.
                  </Typography>
                </Stack>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {program.bands?.map((band) => (
                    <Grid item xs={12} md={4} key={band.code}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #e0e6f0',
                          backgroundColor: '#fff',
                          height: '100%',
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={800}>{band.title}</Typography>
                        <Typography variant="body2" sx={{ color: '#46546b', mb: 1 }}>
                          {band.description}
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1}>
                          {band.rewards?.map((row, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1,
                                borderRadius: 1.5,
                                backgroundColor: '#f9fbff',
                              }}
                            >
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{rangeLabel(row.min_deposit, row.max_deposit)}</Typography>
                                <Typography variant="caption" color="text.secondary">Total bonus {row.total_pct}%</Typography>
                              </Box>
                              <Box textAlign="right">
                                <Typography variant="body2" fontWeight={700} color="#04477e">You {row.you_pct}%</Typography>
                                <Typography variant="caption" color="text.secondary">Friend {row.friend_pct}%</Typography>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                        {band.extras?.length ? (
                          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {band.extras.map((extra, idx) => (
                              <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                • {extra}
                              </Typography>
                            ))}
                          </Stack>
                        ) : null}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

          </Grid>

        </Grid>

        {program && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              mt: 2,
              borderRadius: 3,
              border: '1px solid #e6ebf5',
              backgroundColor: '#fff',
              maxWidth: 1180,
              mx: 'auto',
            }}
          >
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid #dfe5f2',
                    background: 'linear-gradient(135deg, #fbfdff 0%, #f4f7ff 100%)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Minimum Lot Cycle & Fair Play Rules
                    </Typography>
                  </Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f6f8fc' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Deposit</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Min lots</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {program.lot_requirements?.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{rangeLabel(row.min_deposit, row.max_deposit)}</TableCell>
                          <TableCell align="right">{row.lots}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    {program.rules?.map((rule, idx) => (
                      <Stack key={idx} direction="row" spacing={1.2} alignItems="flex-start">
                        <InfoOutlinedIcon fontSize="small" sx={{ color: '#04477e', mt: '2px' }} />
                        <Typography variant="body2" color="text.secondary">
                          {rule}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  id="referral-form"
                  elevation={0}
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3.5,
                    border: '1px solid #0d5ba8',
                    background: 'linear-gradient(135deg, #04477e 0%, #0d5ba8 100%)',
                    boxShadow: '0 18px 40px rgba(4, 71, 126, 0.16)',
                    height: '100%',
                    color: '#f7fbff',
                    fontFamily: '"Poppins", "Inter", system-ui, -apple-system, sans-serif',
                  }}
                >
                  <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: 0.2 }}>
                      Add a friend
                    </Typography>
                    <Chip
                        label="Earn together"
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          borderColor: 'rgba(255,255,255,0.6)',
                          color: '#fff',
                          fontWeight: 700,
                          backgroundColor: 'rgba(255,255,255,0.08)',
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                      A quick intro helps the success team fast-track verification and bonuses for both sides.
                    </Typography>
                    {error && (
                      <Alert
                        severity="error"
                        onClose={() => setError('')}
                        sx={{ borderRadius: 2, backgroundColor: '#fdecea', color: '#5f2120' }}
                      >
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert
                        severity="success"
                        onClose={() => setSuccess('')}
                        sx={{ borderRadius: 2, backgroundColor: '#e9f6ec', color: '#1e4620' }}
                      >
                        {success}
                      </Alert>
                    )}
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ color: '#e8f1ff', fontWeight: 700 }}>
                        Friend name
                      </Typography>
                      <TextField
                        value={form.friend_name}
                        onChange={handleChange('friend_name')}
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#f8fbff',
                            borderRadius: 2,
                            color: '#0a2e4f',
                          },
                        }}
                      />
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ color: '#e8f1ff', fontWeight: 700 }}>
                        Friend email
                      </Typography>
                      <TextField
                        type="email"
                        value={form.friend_email}
                        onChange={handleChange('friend_email')}
                        fullWidth
                        placeholder="name@example.com"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#f8fbff',
                            borderRadius: 2,
                            color: '#0a2e4f',
                          },
                        }}
                      />
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ color: '#e8f1ff', fontWeight: 700 }}>
                        Phone (optional)
                      </Typography>
                      <TextField
                        value={form.friend_phone}
                        onChange={handleChange('friend_phone')}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#f8fbff',
                            borderRadius: 2,
                            color: '#0a2e4f',
                          },
                        }}
                      />
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" sx={{ color: '#e8f1ff', fontWeight: 700 }}>
                        Note for the success team
                      </Typography>
                      <TextField
                        multiline
                        minRows={2}
                        value={form.note}
                        onChange={handleChange('note')}
                        fullWidth
                        placeholder="Anything that helps us welcome them faster (language, target deposit, etc.)"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#f8fbff',
                            borderRadius: 2,
                            color: '#0a2e4f',
                          },
                        }}
                      />
                    </Stack>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      startIcon={<SendIcon />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        px: 2.5,
                        backgroundColor: '#f8fbff',
                        color: '#04477e',
                        '&:hover': { backgroundColor: '#e6f0ff' },
                      }}
                    >
                      {submitting ? 'Sending...' : 'Submit referral'}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: '1px solid #e6ebf5' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} rowGap={1}>
            <Typography variant="h6" fontWeight={700}>My referrals</Typography>
            <Typography variant="body2" color="text.secondary">
              We calculate projected rewards using your current tier and the deposit band.
            </Typography>
          </Stack>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Friend</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Deposit</TableCell>
                <TableCell align="right">Your share</TableCell>
                <TableCell align="right">Friend share</TableCell>
                <TableCell align="right">Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals?.length ? (
                referrals.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{r.friend_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.friend_email || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel(r.status)}
                        color={STATUS_COLOR[r.status] || 'default'}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatMoney(r.investment_amount)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.projected_user_bonus)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.projected_friend_bonus)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No referrals yet. Add your first friend to unlock the programme.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        {program?.faqs?.length ? (
          <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 3, border: '1px solid #e6ebf5' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              FAQs
            </Typography>
            {program.faqs.map((faq, idx) => (
              <Accordion key={idx} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" fontWeight={700}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: '#41506b' }}>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        ) : null}
      </Box>
    </Box>
  );
};

export default FriendReferralsPage;
